import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { CheckCircle } from 'lucide-react';
import LessonQuizPlayer from '../LessonQuizPlayer';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Backward-compat: quiz sorularini flat ya da nested alandan al.
 * @param {Object} lesson
 * @returns {Array}
 */
function getQuizQuestions(lesson) {
  return lesson.quiz_questions || lesson.quiz?.questions || lesson.questions || [];
}

/**
 * InteractiveLesson — LessonQuizPlayer bazli interaktif ders.
 * Quiz gecilince complete-lesson cagirilir; onComplete callback tetiklenir.
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function InteractiveLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const lessonId = lesson.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);
  const questions = getQuizQuestions(lesson);
  const passing = lesson.quiz_passing_score || 70;

  const handleQuizComplete = async (score) => {
    if (score < passing) {
      toast.warning(
        lang === 'tr'
          ? `%${score} aldin, gecmek icin %${passing} gerekli`
          : `Score ${score}%, need ${passing}% to pass`
      );
      return;
    }
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: score },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? `Harika! %${score} aldin` : `Great! Score: ${score}%`);
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  if (isCompleted) {
    return (
      <div
        className="card-clay p-6 mb-6 text-center bg-green-50 border-green-200"
        data-testid="interactive-quiz-done"
      >
        <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
        <p className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu interaktif dersi tamamladin' : 'You completed this interactive lesson'}
        </p>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="card-clay p-8 text-center text-[var(--text-muted)]" data-testid="interactive-no-questions">
        <p className="font-semibold">
          {lang === 'tr' ? 'Bu interaktif ders için soru bulunamadı.' : 'No questions found for this interactive lesson.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="interactive-quiz-wrapper">
      <LessonQuizPlayer
        questions={questions}
        lang={lang}
        onComplete={(score) => handleQuizComplete(score)}
        onFail={() =>
          toast.error(lang === 'tr' ? 'Canlarin bitti, tekrar dene' : 'Out of lives, try again')
        }
      />
    </div>
  );
}
